import { createRouter, createWebHistory } from 'vue-router'
import IntroductionView from '@/views/IntroductionView.vue'
import MainLayout from '@/layouts/MainLayout.vue'
import { useAuthStore } from '@/stores/auth'
import { Message } from 'view-ui-plus'

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes: [
    // 不需要布局的页面
    {
      path: '/',
      name: 'introduction',
      component: IntroductionView
    },
    {
      path: '/login',
      name: 'login',
      component: () => import('@/views/LoginView.vue')
    },
    {
      path: '/register',
      name: 'register',
      component: () => import('@/views/RegisterView.vue')
    },
    {
      path: '/reset-password',
      name: 'reset-password',
      component: () => import('@/views/ResetPasswordView.vue')
    },
    // 独立的账户设置页面（不使用主布局）
    {
      path: '/account-settings',
      name: 'account-settings',
      component: () => import('@/views/AccountSettingsView.vue'),
      meta: { requiresAuth: true }
    },
    // API 文档页面
    {
      path: '/openapi-docs',
      name: 'openapi-docs',
      component: () => import('@/views/ApiDocView.vue'),
      meta: { requiresAuth: true }
    },
    // 研究报告编辑和预览（不使用主布局，全屏显示）
    {
      path: '/research-reports/:id/edit',
      name: 'research-report-edit',
      component: () => import('@/views/research-reports/ResearchReportEditor.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/research-reports/new',
      name: 'research-report-new',
      component: () => import('@/views/research-reports/ResearchReportEditor.vue'),
      meta: { requiresAuth: true }
    },
    {
      path: '/research-reports/:id/view',
      name: 'research-report-view',
      component: () => import('@/views/research-reports/ResearchReportViewer.vue'),
      meta: { requiresAuth: true }
    },
    // 管理员页面
    {
      path: '/admin',
      component: () => import('@/layouts/AdminLayout.vue'),
      meta: { requiresAuth: true, requiresAdmin: true },
      children: [
        {
          path: '',
          redirect: { name: 'admin-dashboard' }
        },
        {
          path: 'dashboard',
          name: 'admin-dashboard',
          component: () => import('@/views/admin/DashboardView.vue')
        },
        {
          path: 'users',
          name: 'admin-user-management',
          component: () => import('@/views/admin/UserManagementView.vue'),
          meta: { title: '用户管理' }
        },
        {
          path: 'feedback-management',
          name: 'admin-feedback-management',
          component: () => import('@/views/admin/FeedbackManagementView.vue')
        },
        {
          path: 'coupon-management',
          name: 'admin-coupon-management',
          component: () => import('@/views/admin/CouponManagementView.vue')
        },
        {
          path: 'products',
          name: 'admin-product-management',
          component: () => import('@/views/admin/ProductManagementView.vue'),
          meta: { title: '会员价格管理' }
        }
      ]
    },
    // 使用主布局的页面
    {
      path: '/app',
      component: MainLayout,
      meta: { requiresAuth: true },
      children: [
        {
          path: 'home',
          name: 'home',
          component: () => import('@/views/MacroEconomyDashboard.vue')
        },

        {
          path: 'policies',
          name: 'policies',
          component: () => import('@/views/PoliciesView.vue')
        },
        {
          path: 'research-reports',
          name: 'research-reports',
          component: () => import('@/views/research-reports/ResearchReportListPage.vue')
        },
        {
          path: 'analysis',
          name: 'analysis',
          component: () => import('@/views/AnalysisView.vue')
        },
        {
          path: 'companies',
          name: 'companies',
          component: () => import('@/views/CompaniesView.vue')
        },
        {
          path: 'company-detail/:code',
          name: 'company-detail',
          component: () => import('@/views/CompanyDetailView.vue'),
          props: true
        },
      ]
    }
  ]
})

router.beforeEach(async (to, _from, next) => {
  const authStore = useAuthStore()

  // 对于需要认证的路由，确保认证状态是最新的
  if (to.meta.requiresAuth) {
    // 如果store中没有用户信息或未认证，重新初始化
    if (!authStore.user || !authStore.isAuthenticated) {
      await authStore.initAuth()
    }

    // 再次检查认证状态
    if (!authStore.isAuthenticated) {
      next({
        path: '/login',
        query: { redirect: to.fullPath }
      })
      return
    }

    // 检查管理员权限
    if (to.meta.requiresAdmin) {
      const isAdmin = authStore.roles.includes('admin')
      if (!isAdmin) {
        Message.warning('您没有权限访问此页面')
        next('/app/home')
        return
      }
    }
  }

  // 如果已经登录，访问登录或注册页面时重定向到首页
  if ((to.name === 'login' || to.name === 'register') && authStore.isAuthenticated) {
    // 确保认证状态是最新的
    if (!authStore.user) {
      await authStore.initAuth()
    }

    if (authStore.isAuthenticated) {
      next('/app/home')
      return
    }
  }

  next()
})

export default router